import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '@guryou/colors';
import { Block, CheckBox, ContainedButton, FlexRow, HiddenField } from '@guryou/html-components';
import { borderDefinition } from '@guryou/html-theme';
import { ALL_DAYS_NO, ALL_DAYS_YES } from '@guryou/redux';
import './ai.css';

const WorkoutScheduleCalendar = ({ workoutSchedule, workoutSchedules = [], onChange, hideEdit = false }) => {
  const { t } = useTranslation();
  const days = [1, 2, 3, 4, 5, 6, 7];
  const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const dayShortLabels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  const allSchedules =
    workoutSchedules.length > 0 ? [workoutSchedule, ...workoutSchedules].filter(Boolean) : workoutSchedule ? [workoutSchedule] : [];

  const [schedule, setSchedule] = useState(workoutSchedule || {});
  const [clickedWeek, setClickedWeek] = useState(1);
  const [allDays, setAllDays] = useState([]);

  const getAllWeeks = () => {
    if (allSchedules.length <= 1) {
      return Object.keys(schedule).sort((a, b) => +a - +b);
    }

    const weeksSet = new Set();
    allSchedules.forEach(sched => {
      if (sched) {
        Object.keys(sched).forEach(week => weeksSet.add(week));
      }
    });
    return Array.from(weeksSet).sort((a, b) => +a - +b);
  };

  const weeks = getAllWeeks();
  const useCompactLayout = weeks.length < 7;

  useEffect(() => {
    setSchedule(workoutSchedule || {});
  }, [workoutSchedule]);

  useEffect(() => {
    if (onChange) {
      onChange(schedule);
    }
  }, [schedule, onChange]);

  const handleWeekChange = week => {
    setClickedWeek(+week);
  };

  const handleDayClick = (day, checked) => {
    const updatedSchedule = { ...schedule };

    if (!updatedSchedule[clickedWeek]) {
      updatedSchedule[clickedWeek] = [];
    }

    if (checked) {
      if (!updatedSchedule[clickedWeek].includes(day)) {
        updatedSchedule[clickedWeek] = [...updatedSchedule[clickedWeek], day];
      }
    } else {
      updatedSchedule[clickedWeek] = updatedSchedule[clickedWeek].filter(d => d !== day);
    }

    setSchedule(updatedSchedule);
  };

  const handleAllDaysClick = checked => {
    const updatedSchedule = { ...schedule };
    const updatedAllDays = [...allDays];

    if (!updatedSchedule[clickedWeek]) {
      updatedSchedule[clickedWeek] = [];
    }

    if (checked) {
      updatedSchedule[clickedWeek] = [...ALL_DAYS_YES];
      updatedAllDays[clickedWeek - 1] = true;
    } else {
      updatedSchedule[clickedWeek] = [...ALL_DAYS_NO];
      updatedAllDays[clickedWeek - 1] = false;
    }

    setSchedule(updatedSchedule);
    setAllDays(updatedAllDays);
  };

  const hasMultipleWorkouts = (week, day) => {
    if (allSchedules.length <= 1) {
      return false;
    }

    let count = 0;
    allSchedules.forEach(sched => {
      if (sched && sched[week] && sched[week].includes(day)) {
        count++;
      }
    });
    return count > 1;
  };

  const isActive = (week, day) => {
    if (allSchedules.length <= 1) {
      return schedule[week] && schedule[week].includes(day);
    }

    return allSchedules.some(sched => sched && sched[week] && sched[week].includes(day));
  };

  if (!weeks.length) {
    return null;
  }

  return (
    <div className="workout-schedule-container-ai">
      {useCompactLayout ? (
        <div className="workout-schedule-grid-ai">
          <div className="workout-schedule-title-ai">{t('WORKOUTPLANS.schedule')}</div>
          <div className="workout-schedule-header-ai">
            <div className="workout-schedule-day-label-ai"></div>
            {days.map((day, index) => (
              <div key={day} className="workout-schedule-week-label-ai">
                {dayShortLabels[index]}
              </div>
            ))}
          </div>

          {weeks.map(week => (
            <div key={week} className="workout-schedule-row-ai">
              <div className="workout-schedule-day-label-ai">Week {week}</div>
              {days.map(day => {
                const active = isActive(week, day);
                const multiple = hasMultipleWorkouts(week, day);
                return <div key={`${week}-${day}`} className={`workout-schedule-cell-ai ${active ? (multiple ? 'multiple' : 'active') : ''}`}></div>;
              })}
            </div>
          ))}
        </div>
      ) : (
        <div className="workout-schedule-grid-ai">
          <div className="workout-schedule-title-ai">{t('WORKOUTPLANS.schedule')}</div>
          <div className="workout-schedule-header-ai">
            <div className="workout-schedule-day-label-ai"></div>
            {weeks.map(week => (
              <div key={week} className="workout-schedule-week-label-ai">
                {week}
              </div>
            ))}
          </div>

          {days.map((day, index) => (
            <div key={day} className="workout-schedule-row-ai">
              <div className="workout-schedule-day-label-ai">{dayLabels[index]}</div>
              {weeks.map(week => {
                const active = isActive(week, day);
                const multiple = hasMultipleWorkouts(week, day);
                return <div key={`${week}-${day}`} className={`workout-schedule-cell-ai ${active ? (multiple ? 'multiple' : 'active') : ''}`}></div>;
              })}
            </div>
          ))}
        </div>
      )}
      {!hideEdit && (
        <>
          <FlexRow xs={12} marginTop={2} alignItems="center" justifyContent="center">
            {weeks.map(week => (
              <ContainedButton
                key={`week-btn-${week}`}
                color={
                  clickedWeek === +week ? 'primary' : schedule[week] ? (schedule[week].some(w => !!w) ? colors.fruitSalad : 'inherit') : 'inherit'
                }
                text={`${t('CALENDAR.week')} ${week} (${schedule[week] ? schedule[week].filter(w => !!w).length : 0}/7)`}
                onClick={() => handleWeekChange(week)}
              />
            ))}
          </FlexRow>

          <FlexRow xs={12} marginTop={2} alignItems="center">
            <FlexRow xs={12} justifyContent="center" alignItems="center" borderBottom={borderDefinition()}>
              <CheckBox
                xs={2}
                margin="0 auto"
                className="myWorkoutCheckBox"
                label={t(`CALENDAR.allDays`)}
                checked={allDays[clickedWeek - 1] || false}
                onChange={checked => handleAllDaysClick(checked)}
              />
            </FlexRow>

            {days.map((day, idx) => (
              <Block xs={12 / 7} key={`day-check-${day}`}>
                <CheckBox
                  className="myWorkoutCheckBox"
                  value={day}
                  checked={schedule[clickedWeek] ? schedule[clickedWeek].includes(day) : false}
                  label={t(`GENERAL.days_${day}`)}
                  margin="none"
                  number="true"
                  onChange={checked => handleDayClick(day, checked, idx)}
                />
              </Block>
            ))}
          </FlexRow>
        </>
      )}

      <HiddenField name="workoutSchedule" value={JSON.stringify(schedule)} />
    </div>
  );
};

export default WorkoutScheduleCalendar;
